/**
 * Product page behaviour added in the 2026-10-29 feedback round. Everything
 * here sits on top of Dawn's own product code (product-info.js,
 * media-gallery.js, product-form.js) and talks to it through its public
 * methods and markup, so variant selection, the gallery swap on a colour change
 * and add-to-cart stay Dawn's.
 *
 *   <squish-gallery-arrows>  previous / next over the main image
 *   <squish-share>           native share, else a WhatsApp + copy-link menu
 *   <squish-buy-now>         "Buy Now": add the chosen variant, go to checkout
 *   <squish-pincode>         PIN code validation and delivery answer
 *
 * Each is a custom element, so it also works inside Quick view, which lifts
 * the product page's markup into a dialog.
 */
(() => {
  if (customElements.get('squish-gallery-arrows')) return;

  const announce = (message) => {
    let live = document.getElementById('SquishProductLive');
    if (!live) {
      live = document.createElement('div');
      live.id = 'SquishProductLive';
      live.className = 'visually-hidden';
      live.setAttribute('role', 'status');
      live.setAttribute('aria-live', 'polite');
      document.body.appendChild(live);
    }
    live.textContent = '';
    setTimeout(() => (live.textContent = message), 30);
  };

  /* ------------------------------------------------------ gallery arrows */

  /**
   * Steps through the media currently rendered in the gallery. The list is read
   * at click time, because a colour change swaps in that colour's images.
   * Moving goes through media-gallery's own setActiveMedia(), which also moves
   * the thumbnail highlight (and scrolls the slider on a phone), so the main
   * image and the thumbnail rail can never disagree.
   */
  class SquishGalleryArrows extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-dir]');
        if (button) this.step(Number(button.dataset.dir));
      });
    }

    items(gallery) {
      // Only the slides (li.product__media-item): Dawn repeats data-media-id,
      // without the section prefix, on the zoom button inside each slide.
      return [...gallery.querySelectorAll('[id^="GalleryViewer"] li.product__media-item[data-media-id]')].filter(
        // Desktop shows one slide at a time (the rest are display:none), so
        // visibility is no test. Only slides Dawn hides for good are skipped:
        // other variants' images when "Hide other colours' images" is on.
        (item) => !item.classList.contains('product__media-item--variant') || item.classList.contains('is-active')
      );
    }

    currentIndex(gallery, items) {
      const desktop = window.matchMedia('(min-width: 750px)').matches;
      if (!desktop) {
        // On a phone the viewer is a horizontal scroller: the current image is
        // the one nearest the scroll position, whatever was tapped before.
        const scroller = items[0]?.parentElement;
        if (scroller) {
          let best = 0;
          let distance = Infinity;
          items.forEach((item, i) => {
            const d = Math.abs(item.offsetLeft - scroller.scrollLeft);
            if (d < distance) {
              distance = d;
              best = i;
            }
          });
          return best;
        }
      }
      const current = gallery.querySelector('[id^="GalleryThumbnails"] button[aria-current="true"]')?.closest('[data-target]');
      if (current) {
        const index = items.findIndex((item) => item.dataset.mediaId === current.dataset.target);
        if (index !== -1) return index;
      }
      const active = items.findIndex((item) => item.classList.contains('is-active'));
      return active === -1 ? 0 : active;
    }

    step(direction) {
      const gallery = this.closest('media-gallery');
      if (!gallery || typeof gallery.setActiveMedia !== 'function') return;
      const items = this.items(gallery);
      if (items.length < 2) return;
      const next = (this.currentIndex(gallery, items) + direction + items.length) % items.length;
      gallery.setActiveMedia(items[next].dataset.mediaId, false);
      announce(`Image ${next + 1} of ${items.length}`);
    }
  }
  customElements.define('squish-gallery-arrows', SquishGalleryArrows);

  /* --------------------------------------------------------------- share */

  /**
   * Shares the product's canonical URL (plus the selected colour/size, so a
   * friend lands on the same variant). The device's own share sheet is used
   * where the browser offers one; otherwise a small menu offers WhatsApp and
   * Copy link.
   */
  class SquishShare extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('.sq-share__button');
      this.menu = this.querySelector('.sq-share__menu');
      this.copyButton = this.querySelector('[data-share-copy]');
      this.whatsapp = this.querySelector('[data-share-whatsapp]');
      if (!this.button || !this.menu) return;

      this.button.addEventListener('click', () => this.share());
      this.copyButton?.addEventListener('click', () => this.copy());
      this.whatsapp?.addEventListener('click', () => this.close(false));

      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.close(false);
      };
      this.onKeydown = (event) => {
        if (event.key === 'Escape' && !this.menu.hidden) {
          event.stopPropagation();
          this.close(true);
        }
      };
      this.addEventListener('keydown', this.onKeydown);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
    }

    url() {
      try {
        const url = new URL(this.dataset.url, window.location.origin);
        const variant = new URL(window.location.href).searchParams.get('variant');
        if (variant) url.searchParams.set('variant', variant);
        return url.toString();
      } catch {
        return window.location.href;
      }
    }

    async share() {
      const data = { title: this.dataset.title || document.title, url: this.url() };
      if (navigator.share && (!navigator.canShare || navigator.canShare(data))) {
        try {
          await navigator.share(data);
          return;
        } catch (error) {
          // The shopper closed the sheet: nothing to do.
          if (error && error.name === 'AbortError') return;
          // Anything else (for example a permissions policy): use the menu.
        }
      }
      if (this.menu.hidden) this.open();
      else this.close(true);
    }

    open() {
      if (this.whatsapp) {
        this.whatsapp.href = `https://wa.me/?text=${encodeURIComponent(`${this.dataset.title || ''} ${this.url()}`.trim())}`;
      }
      this.menu.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', this.onDocumentClick);
      (this.whatsapp || this.copyButton)?.focus();
    }

    close(returnFocus) {
      if (this.menu.hidden) return;
      this.menu.hidden = true;
      this.button.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.onDocumentClick);
      if (returnFocus) this.button.focus();
    }

    async copy() {
      const url = this.url();
      let copied = false;
      try {
        await navigator.clipboard.writeText(url);
        copied = true;
      } catch {
        // Older browsers, or clipboard permission refused: a selected field
        // and the legacy copy command.
        const field = document.createElement('textarea');
        field.value = url;
        field.setAttribute('readonly', '');
        field.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
        document.body.appendChild(field);
        field.select();
        try {
          copied = document.execCommand('copy');
        } catch {
          copied = false;
        }
        field.remove();
      }
      const label = this.copyButton.querySelector('span');
      if (copied) {
        label.textContent = 'Link copied';
        announce('Link copied');
        setTimeout(() => {
          label.textContent = 'Copy link';
          this.close(true);
        }, 1400);
      } else {
        // Last resort: show the address so it can be copied by hand.
        label.textContent = url;
        announce('Copying is blocked in this browser. The link is shown instead.');
      }
    }
  }
  customElements.define('squish-share', SquishShare);

  /* ------------------------------------------------------------- buy now */

  /**
   * Mirrors the Add to cart button's disabled state (Dawn toggles it for sold
   * out and unavailable combinations), so Buy Now is never usable when Add to
   * cart is not. On click it adds the selected variant and quantity through the
   * Ajax cart and opens checkout; any error is shown in Dawn's own product-form
   * error area.
   */
  class SquishBuyNow extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.submit = document.getElementById(this.dataset.submitId);
      if (!this.button) return;
      if (this.submit) {
        const sync = () => {
          this.button.disabled = this.submit.hasAttribute('disabled') || this.submit.getAttribute('aria-disabled') === 'true';
        };
        this.observer = new MutationObserver(sync);
        this.observer.observe(this.submit, { attributes: true, attributeFilter: ['disabled', 'aria-disabled'] });
        sync();
      }
      this.button.addEventListener('click', () => this.buy());
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    showError(message) {
      const productForm = this.closest('product-form');
      const wrapper = productForm?.querySelector('.product-form__error-message-wrapper');
      const text = wrapper?.querySelector('.product-form__error-message');
      if (wrapper && text) {
        text.textContent = message;
        wrapper.hidden = false;
      } else {
        announce(message);
      }
    }

    async buy() {
      if (this.button.disabled) return;
      const form = this.closest('form');
      const id = form?.querySelector('input[name="id"]')?.value;
      if (!id) return this.showError('Please choose a colour and size.');
      const quantityInput = form.id ? document.querySelector(`input[name="quantity"][form="${CSS.escape(form.id)}"]`) : null;
      const quantity = Math.max(1, parseInt(quantityInput?.value, 10) || 1);

      this.button.setAttribute('aria-busy', 'true');
      this.button.classList.add('loading');
      this.button.querySelector('.loading__spinner')?.classList.remove('hidden');
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity }] }),
        });
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.description || body.message || 'This item could not be added.');
        window.location.href = `${root}checkout`;
      } catch (error) {
        this.showError(error.message || 'This item could not be added.');
        this.button.removeAttribute('aria-busy');
        this.button.classList.remove('loading');
        this.button.querySelector('.loading__spinner')?.classList.add('hidden');
      }
    }
  }
  customElements.define('squish-buy-now', SquishBuyNow);

  /* ------------------------------------------------------------- pincode */

  const PIN = /^[1-9][0-9]{5}$/;

  class SquishPincode extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.input = this.querySelector('input[name="pincode"]');
      this.result = this.querySelector('.sq-pin__result');
      this.button = this.querySelector('.sq-pin__check');
      if (!this.form || !this.input) return;

      // Digits only, at most six, as the shopper types or pastes.
      this.input.addEventListener('input', () => {
        const digits = this.input.value.replace(/\D/g, '').slice(0, 6);
        if (digits !== this.input.value) this.input.value = digits;
        if (this.input.getAttribute('aria-invalid') === 'true' && PIN.test(digits)) this.clear();
      });
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.check();
      });
    }

    clear() {
      this.input.removeAttribute('aria-invalid');
      this.result.className = 'sq-pin__result';
      this.result.textContent = '';
    }

    show(state, html) {
      this.result.className = `sq-pin__result sq-pin__result--${state}`;
      this.result.innerHTML = html;
    }

    busy(on) {
      this.button.disabled = on;
      this.button.setAttribute('aria-busy', on ? 'true' : 'false');
      this.button.querySelector('.loading__spinner')?.classList.toggle('hidden', !on);
    }

    escape(value) {
      return String(value ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
    }

    range(min, max) {
      const a = Number(min);
      const b = Number(max);
      if (!a && !b) return '';
      if (!b || a === b) return `${a || b}`;
      return `${a}–${b}`;
    }

    async check() {
      const pin = this.input.value.trim();
      if (!PIN.test(pin)) {
        this.input.setAttribute('aria-invalid', 'true');
        this.show('invalid', 'Enter a valid 6-digit PIN code, for example 110001.');
        this.input.focus();
        return;
      }
      this.input.removeAttribute('aria-invalid');

      const endpoint = (this.dataset.endpoint || '').trim();
      if (endpoint) return this.checkService(endpoint, pin);
      this.answerFromPolicy(pin);
    }

    /** No delivery service connected: the published shipping policy, nothing more. */
    answerFromPolicy(pin) {
      const d = this.dataset;
      const dispatch = this.range(d.dispatchMin, d.dispatchMax);
      const transit = this.range(d.transitMin, d.transitMax);
      const parts = [];
      if (d.freeShipping === 'true') parts.push('<strong>Free shipping</strong> on every order across India.');
      if (dispatch && transit) {
        parts.push(`Orders are usually dispatched within ${dispatch} working days and delivered ${transit} working days after dispatch.`);
      } else if (dispatch) {
        parts.push(`Orders are usually dispatched within ${dispatch} working days.`);
      }
      parts.push(`Delivery to <strong>${this.escape(pin)}</strong> is confirmed at checkout.`);
      const policy = d.policyUrl ? ` <a href="${this.escape(d.policyUrl)}">Shipping policy</a>` : '';
      this.show('info', `${parts.join(' ')}${policy}`);
    }

    /** A connected courier service, through a same-origin endpoint only. */
    async checkService(endpoint, pin) {
      // A theme setting is public: only a same-origin path (such as an app
      // proxy) is called, so no third-party key can be wired in here.
      if (!endpoint.startsWith('/') || endpoint.startsWith('//')) {
        this.show('error', 'Delivery check is not available right now. Delivery is confirmed at checkout.');
        return;
      }
      this.busy(true);
      this.show('loading', 'Checking delivery…');
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 8000);
      try {
        const url = new URL(endpoint, window.location.origin);
        url.searchParams.set('pincode', pin);
        const response = await fetch(url, { headers: { Accept: 'application/json' }, signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (typeof data.serviceable !== 'boolean') throw new Error('Malformed response');

        if (data.serviceable) {
          const eta = this.range(data.eta_min_days, data.eta_max_days);
          const message = data.message ? this.escape(data.message) : `Delivery available to <strong>${this.escape(pin)}</strong>.`;
          // The courier's own estimate (calendar days), shown only when it sent one.
          this.show('ok', `${message}${eta ? ` Estimated delivery in ${eta} days.` : ''}`);
        } else {
          const message = data.message ? this.escape(data.message) : `Sorry, we cannot deliver to <strong>${this.escape(pin)}</strong> yet.`;
          this.show('unavailable', message);
        }
      } catch {
        this.show('error', 'We could not check that PIN code just now. Please try again; delivery is also confirmed at checkout.');
      } finally {
        clearTimeout(timer);
        this.busy(false);
      }
    }
  }
  customElements.define('squish-pincode', SquishPincode);
})();
